import React, { FC, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { SortableHandle } from 'react-sortable-hoc';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Button
} from '@mui/material';
import { ExpandMore, Edit, Delete, DragIndicator, Visibility } from '@mui/icons-material';
import { ImageModal } from '../../../components';
// Types
import { ISheetTask } from '../../../models';

interface ITaskTemplateModalMobile {
  original: ISheetTask;
  handleEdit: (val: number) => void;
  handleDelete: (val: ISheetTask) => void;
}

const DragHandle = SortableHandle(() => {
  const classes = useStyles();
  return <DragIndicator className={classes.dragIcon} />;
});

export const TaskTemplateModalMobile: FC<ITaskTemplateModalMobile> = ({
  original,
  handleEdit,
  handleDelete,
  ...rest
}) => {
  const classes = useStyles();
  const { text, categories, assignedTo, subscribers, images } = original;
  const [selectedImgSource, setSelectedImgSource] = useState('');
  return (
    <>
      <Accordion className={classes.root}>
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="body2">{text}</Typography>
            <div className={classes.buttonsWrapper}>
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  // @ts-ignore
                  handleEdit(rest.index);
                }}
                size="large"
              >
                <Edit />
              </IconButton>
              <IconButton
                className={classes.deleteButton}
                color="default"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(original);
                }}
                size="large"
              >
                <Delete />
              </IconButton>
              <DragHandle />
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Text:
            </FormLabel>
            <Typography component="span" variant="body2">
              {text || '--'}
            </Typography>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Categories:
            </FormLabel>
            <Typography component="span" variant="body2">
              {categories && categories.length > 0
                ? categories.map(cat => cat.name).join(', ')
                : '--'}
            </Typography>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Assigned To:
            </FormLabel>
            <Typography component="span" variant="body2">
              {assignedTo || '--'}
            </Typography>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Subscribers:
            </FormLabel>
            <Typography component="span" variant="body2">
              {subscribers && subscribers.length > 0 ? subscribers.join(', ') : '--'}
            </Typography>
          </div>
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Images:
            </FormLabel>
            <Typography component="span" variant="body2">
              {images && images.length > 0
                ? images.map((image, index) => {
                    return (
                      <Button
                        startIcon={<Visibility />}
                        variant="text"
                        color="secondary"
                        key={`${index}`}
                        onClick={() => setSelectedImgSource(image)}
                      >
                        image.png{index > 0 ? ` - ${index + 1}` : ''}
                      </Button>
                    );
                  })
                : '--'}
            </Typography>
          </div>
        </AccordionDetails>
      </Accordion>
      <ImageModal
        isOpen={!!selectedImgSource}
        onClose={() => setSelectedImgSource('')}
        source={selectedImgSource}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden',
    zIndex: 1500
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  dragIcon: {
    background: 'white',
    fill: theme.palette.grey[600],
    fontSize: '2rem',
    cursor: 'grab'
  }
}));
